/* eslint-disable */

import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQuery } from 'urql'
import { useTranslation } from 'react-i18next'
import { CreateProduct, IssueProductToClient, RemoveProductSpot, SetProductSpot } from '../graphql/mutations'
import { GetProductByCode } from '../graphql/queries'
import ScreenHeader from './ScreenHeader'
import Button from './Button'
import ScanIcon from './icons/scan'
import LoadingComponent from './Loading'
import DeleteIcon from './icons/delete'
import ScannerInput from './ScannerInput'
import { Responses, PackagingType, PackagingDefaults, isDev } from '../util/consts'
import { packagingShortLabel } from '../util/helpers'
import Input from './Input'

const STORAGE_KEY = 'warehouse_default_pkg'

const loadDefaultPkg = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
            const parsed = JSON.parse(raw)
            if (parsed?.type && PackagingDefaults[parsed.type]) return parsed
        }
    } catch (e) {}
    return PackagingDefaults.BIGBAG
}

const pkgLabel = (p) => {
    if (!p?.type) return ''
    if (p.type === PackagingType.BigBag) return `BB${p.weight}`
    if (p.type === PackagingType.Sb25) return `SB25/${p.bagCount}`
    if (p.type === PackagingType.Sb50) return `SB50/${p.bagCount}`
    return ''
}

const WarehouseScan = ({
    warehouse,
    onBack,
}) => {
    const { t } = useTranslation()
    const [code, setCode] = useState(isDev? '1234567890' : '')
    const [hasScanned, setHasScanned] = useState(false)
    const [defaultPkg, setDefaultPkg] = useState(loadDefaultPkg)
    const [editView, setEditView] = useState(null) // null | 'menu' | 'BIGBAG' | 'SB25' | 'SB50'
    const [editValue, setEditValue] = useState('')
    const [spotError, setSpotError] = useState('')
    const [previousSpot, setPreviousSpot] = useState(null)
    const [, setSpot] = useMutation(SetProductSpot)
    const [, removeSpot] = useMutation(RemoveProductSpot)
    const [, issueToClient] = useMutation(IssueProductToClient)
    const [, addProduct] = useMutation(CreateProduct)
    const [productResult, refetchProduct] = useQuery({
        query: GetProductByCode,
        variables: {
            code,
        },
        requestPolicy: 'network-only',
    })

    const product = productResult.data?.getProductByCode

    // Packaging type of the pallet being placed: an already-created product keeps
    // its own type, otherwise we use the operator's current default.
    const currentPkgType = product?.code ? product.packagingType : defaultPkg.type

    const handleInput = (input) => {
        setHasScanned(true)
        setSpotError('')
        setCode(input)
    }

    const persistDefault = (pkg) => {
        setDefaultPkg(pkg)
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(pkg))
        } catch (e) {}
    }

    const openEdit = (type) => {
        setEditView(type)
        const base = defaultPkg.type === type ? defaultPkg : PackagingDefaults[type]
        setEditValue(type === PackagingType.BigBag ? base.weight : base.bagCount)
    }

    const saveEdit = (type) => {
        const value = parseInt(editValue, 10)
        if (!value || value < 1) return

        if (type === PackagingType.BigBag) {
            persistDefault({ type, weight: value, bagCount: null })
        } else {
            persistDefault({ type, weight: PackagingDefaults[type].weight, bagCount: value })
        }

        setEditView(null)
    }

    const spotLockedType = (spot) => spot?.products?.find(ps => ps.product?.packagingType)?.product?.packagingType || null

    const isSpotDisabled = (spot) => {
        const locked = spotLockedType(spot)
        return !!locked && locked !== currentPkgType
    }

    const handleSpotClick = async (spot) => {
        if (
            productResult.data?.getProductByCode?.productSpot?.labResult?.bookedProducts?.length
            && productResult.data?.getProductByCode?.productSpot?.labResult?.bookedProducts.some(x => x.amount !== x.sentProducts.length)
            || !spot?.id
        ) return

        if (isSpotDisabled(spot)) {
            setSpotError(t('warehouse.spot_locked'))
            return
        }

        setSpotError('')
        setPreviousSpot(spot)

        let newProduct = null

        if (!productResult.data?.getProductByCode?.code) {
            if (!code || !defaultPkg.weight) return

            // Store the full pallet weight: big bag = its weight, small bags = bag weight * count.
            const palletWeight = defaultPkg.type === PackagingType.BigBag
                ? parseFloat(defaultPkg.weight)
                : parseFloat(defaultPkg.weight) * parseInt(defaultPkg.bagCount, 10)

            const itemData = {
                code,
                weight: palletWeight,
                packagingType: defaultPkg.type,
                bagCount: defaultPkg.bagCount ?? null,
            }

            const { data } = await addProduct(itemData)

            if (
                !data?.createProduct
                || [Responses.Failed, Responses.Exists].includes(data.createProduct)
            ) return

            newProduct = parseInt(data.createProduct)
        }

        const res = await setSpot({
            productId: newProduct || productResult.data?.getProductByCode?.id,
            spotId: parseInt(spot.id),
        })

        console.log(res)

        if (res?.data?.setProductSpot === Responses.SpotLocked) {
            setSpotError(t('warehouse.spot_locked'))
            return
        }

        if (res?.data?.setProductSpot && res.data.setProductSpot === Responses.Success) {
            refetchProduct()
        }
    }

    const handleSpotRemove = async () => {
        const res = await removeSpot({
            productId: productResult.data?.getProductByCode?.id,
        })

        if (res?.data?.removeProductSpot && res.data.removeProductSpot === Responses.Success) {
            refetchProduct()
        }
    }

    const handleIssueClient = async (booking) => {
        const res = await issueToClient({
            productId: productResult.data?.getProductByCode?.id,
            bookId: booking?.id,
        })

        if (res?.data?.issueProductToClient && res.data.issueProductToClient === Responses.Success) {
            refetchProduct()
        }
    }

    const isSelectedSpot = (spotId) => productResult?.data?.getProductByCode?.productSpot?.spot
            && productResult.data.getProductByCode.productSpot.spot.id === spotId ? ' current' : ''

    const renderHeaderActions = () => (
        <>
            <Button
                label={t('warehouse.change_warehouse')}
                onClick={onBack}
            />
        </>
    )

    const renderClients = () => {
        if (!productResult.data?.getProductByCode?.productSpot?.labResult?.bookedProducts?.length) return <></>

        const filteredClients = productResult.data.getProductByCode.productSpot.labResult.bookedProducts.filter(x => x.amount !== x.sentProducts.length)

        return (
            filteredClients.map(i =>
                <Button
                    key={i.id}
                    className={`btn--issue-spot`}
                    label={`${t('warehouse.issue_client')}: ${i.client.name}`}
                    onClick={() => handleIssueClient(i)}
                />
            )
        )
    }

    const renderRemove = () => {
        return (
            !productResult.data?.getProductByCode?.productSpot
            || productResult.data?.getProductByCode?.productSpot?.labResult?.bookedProducts?.length
            && productResult.data?.getProductByCode?.productSpot?.labResult?.bookedProducts.some(x => x.amount !== x.sentProducts.length) ?
            <></>
            :
            <Button
                className={`btn--remove-spot`}
                label={t('warehouse.remove_spot')}
                onClick={handleSpotRemove}
                icon={<DeleteIcon />}
            />
        )
    }

    const renderSpotsGrid = () => (
        <>
            {
                spotError ?
                <p className='warehouse-content--spot-error'>{ spotError }</p>
                :
                <></>
            }

            {
                previousSpot ?
                <div className='warehouse-content--previous-spot'>
                    <p>{ t('warehouse.previous_spot') }</p>
                    <div
                        className={
                            `warehouse-content--product-spot${isSelectedSpot(previousSpot.id)}`
                        }
                        onClick={() => handleSpotClick(previousSpot)}
                    >
                        { previousSpot.name }
                    </div>
                </div>
                :
                <></>
            }

            <p>{ t('warehouse.choose_spot') }</p>
            <div
                className='warehouse-content--product-spots'
            >
                {
                    warehouse.spots.map(i =>
                        <div
                            className={
                                `warehouse-content--product-spot${isSelectedSpot(i.id)}${isSpotDisabled(i) ? ' locked' : ''}`
                            }
                            key={i.id}
                            onClick={() => handleSpotClick(i)}
                        >
                            { i.name }
                        </div>
                    )
                }
            </div>
        </>
    )

    const renderDefaultEditor = () => {
        if (editView === 'menu') {
            return (
                <div className='warehouse-content--default-edit'>
                    <p>{ t('warehouse.change_default') }</p>
                    <Button label={t('warehouse.big_bag')} onClick={() => openEdit(PackagingType.BigBag)} />
                    <Button label={t('warehouse.bags_25')} onClick={() => openEdit(PackagingType.Sb25)} />
                    <Button label={t('warehouse.bags_50')} onClick={() => openEdit(PackagingType.Sb50)} />
                    <Button className='btn--cancel-default' label={t('warehouse.cancel')} onClick={() => setEditView(null)} />
                </div>
            )
        }

        const isBigBag = editView === PackagingType.BigBag

        return (
            <div className='warehouse-content--default-edit'>
                <p>
                    {
                        isBigBag ? t('warehouse.big_bag')
                        : editView === PackagingType.Sb25 ? t('warehouse.bags_25')
                        : t('warehouse.bags_50')
                    }
                </p>
                <Input
                    label={isBigBag ? t('products.weight') : t('warehouse.bag_count')}
                    placeholder={isBigBag ? t('products.weight') : t('warehouse.bag_count')}
                    value={editValue}
                    type={'number'}
                    suffix={isBigBag ? 'kg' : t('warehouse.bag_count_suffix')}
                    onChange={(e) => setEditValue(e.target.value)}
                />
                <Button label={t('warehouse.ok')} onClick={() => saveEdit(editView)} />
            </div>
        )
    }

    const renderDefaultBar = () => (
        <div className='warehouse-content--default'>
            <div className='warehouse-content--default-current'>
                <span>{ t('warehouse.default_package') }</span>
                <strong>{ pkgLabel(defaultPkg) }</strong>
            </div>
            <Button
                className='btn--change-default'
                label={t('warehouse.change_default')}
                onClick={() => setEditView('menu')}
            />
        </div>
    )

    const renderSpots = useMemo(() => {
        if (!code) return (
            <div className='warehouse-content--scan-empty'>
                <ScanIcon />
                <h4>{ t('warehouse.scan') }</h4>
            </div>
        )

        if (productResult?.data?.getProductByCode?.clientProduct?.client) return (
            <div className='warehouse-content--product-issued'>
                <p>
                    { t('warehouse.product_issued', { code: productResult.data.getProductByCode.code }) }
                    <span>{ ` ${productResult.data.getProductByCode.clientProduct.client.name}` }</span>
                </p>
            </div>
        )

        if (!productResult?.data?.getProductByCode?.id) {
            return (
                <div className='warehouse-content--product'>
                    <p>{ `Toote kood: ${code}` }</p>

                    { renderRemove() }

                    { renderClients() }

                    { renderSpotsGrid() }
                </div>
            )
        }

        if (!productResult?.data?.getProductByCode?.code) return <></>

        return (
            <div className='warehouse-content--product'>
                <p>{ `Toote kood: ${productResult.data.getProductByCode.code}` }</p>
                <p className='warehouse-content--product-packaging'>
                    { `${t('dashboard.packaging_type')}: ${packagingShortLabel(productResult.data.getProductByCode.packagingType)}` }
                </p>

                { renderRemove() }

                { renderClients() }

                { renderSpotsGrid() }
            </div>
        )
    }, [productResult.data, code, defaultPkg, spotError, previousSpot, warehouse])

    if (!warehouse) return <></>

    if (productResult.fetching) return <LoadingComponent />

    return (
        <div
            className='warehouse-content--scan'
        >
            <ScreenHeader
                title={`${t('warehouse.current')}: ${warehouse.name}`}
                actions={renderHeaderActions}
            />

            {
                editView ?
                renderDefaultEditor()
                :
                <>
                    { renderDefaultBar() }
                    { renderSpots }
                </>
            }

            <ScannerInput
                onBarcodeScanned={handleInput}
            />
        </div>
    )
}

WarehouseScan.propTypes = {
    warehouse: PropTypes.object,
    onBack: PropTypes.func,
}

export default WarehouseScan
